import { useEffect } from "react";

const DEFAULT_TITLE = "Central Rentals Canada · Landlord Software";
const JSON_LD_ID = "cr-article-jsonld";

/**
 * usePageTitle — sets the document title, meta description, and
 * (optionally) meta keywords for SPA routes. Search engines and
 * browser tabs then show meaningful, per-page metadata.
 */
export function usePageTitle(title, description, keywords) {
  useEffect(() => {
    document.title = title || DEFAULT_TITLE;
    if (description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", description);
    }
    if (keywords) {
      let meta = document.querySelector('meta[name="keywords"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "keywords");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", keywords);
    }
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, keywords]);
}

/**
 * useNoIndex — marks the current page as noindex/nofollow so search
 * engines skip it. Used for subscriber-only "private-link" tools that
 * we deliberately keep out of the public site map.
 */
export function useNoIndex() {
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const previous = meta.getAttribute("content");
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      if (created) meta.remove();
      else if (previous) meta.setAttribute("content", previous);
    };
  }, []);
}

/**
 * useArticleSeo — injects an Article JSON-LD block so search engines
 * (and Google's rich-result carousel) can classify the page as a
 * long-form article. Also lets us surface on "related searches"
 * carousels by declaring `about` and `keywords`.
 *
 * @param {object} article
 *   { headline, description, url, keywords: string[], datePublished }
 */
export function useArticleSeo(article) {
  useEffect(() => {
    if (!article || !article.headline) return undefined;
    const existing = document.getElementById(JSON_LD_ID);
    if (existing) existing.remove();

    const payload = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.headline,
      description: article.description,
      author: {
        "@type": "Organization",
        name: "Central Rentals Canada",
        url: "https://centralrentals.ca",
      },
      publisher: {
        "@type": "Organization",
        name: "Central Rentals Canada",
        logo: {
          "@type": "ImageObject",
          url: "https://centralrentals.ca/logo512.png",
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id":
          article.url ||
          (typeof window !== "undefined" ? window.location.href : ""),
      },
      datePublished: article.datePublished || "2026-02-01",
      dateModified: article.datePublished || "2026-02-01",
      inLanguage: "en-CA",
      keywords: (article.keywords || []).join(", "),
      about: (article.keywords || []).map((k) => ({
        "@type": "Thing",
        name: k,
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = JSON_LD_ID;
    script.text = JSON.stringify(payload);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(JSON_LD_ID);
      if (s) s.remove();
    };
  }, [article]);
}
